import { useEffect, useMemo } from 'react';

import {
  FilterAppliedProps,
  FilterConfig,
  FilterQueryData,
  Filters,
  useCurrentFilter,
} from '@components/filters';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconNotifications } from '@components/icons/notifications';

import { useStore } from 'models/Provider';

import { FilterActionButtons } from './filter-action-buttons';

function FilterApplicationUnreadActivityApplied({ data }: FilterAppliedProps) {
  const { t } = useStore();

  const valueLabel = Array.isArray(data.value)
    ? data.value.map((label) => t(`applicants.filters.unread.${label}`))
    : t(`applicants.filters.unread.${data.value as string}`);

  return (
    <Filters.AppliedFilter
      icon={<IconNotifications />}
      category={t('applicants.filters.unread.title')}
      value={valueLabel}
      comparer={{
        value: data.comparer!,
        label: t('comparers.' + data.comparer).replace('...', ''),
      }}
    />
  );
}

function FilterApplicationUnreadActivityMenu() {
  const { t } = useStore();
  const { state, actions, isMultiComparer, hasValue } = useCurrentFilter();

  useEffect(() => {
    if (!state.comparer) {
      actions.selectComparer('any_of');
    }
  }, [actions, state.comparer]);

  const filterValueItems = [
    'new_application',
    'messages',
    'notes',
    'ratings',
    'assigned_to',
    'files',
  ].map((item) => ({
    value: item,
    label: t('applicants.filters.unread.' + item),
  }));

  return (
    <Filters.List>
      <Filters.ListItem
        heading
        label={t('applicants.filters.unread.title')}
        icon={<IconArrowLeft />}
        onClick={actions.unselectCategory}
      />

      <Filters.ListSeparator />

      <Filters.FilterValueMenu
        items={filterValueItems}
        isMulti={isMultiComparer}
        onSetValue={actions.selectValue}
        state={state}
      />
      <FilterActionButtons disableApply={!hasValue} />
    </Filters.List>
  );
}

export function useFilterApplicationUnreadActivity(): FilterConfig {
  const { t } = useStore();

  return useMemo(
    () => ({
      category: 'unread',
      label: t('applicants.filters.unread.title'),
      icon: <IconNotifications />,
      AppliedFilter: FilterApplicationUnreadActivityApplied,
      Menu: FilterApplicationUnreadActivityMenu,
      toQueryData: (filter) => {
        if (!Array.isArray(filter.value)) return {};

        switch (filter.comparer) {
          case 'any_of':
            let obj: FilterQueryData = {};
            filter.value.forEach((val) => {
              obj[`unread_${val}`] = true;
            });
            return obj;
          default:
            return {};
        }
      },
    }),
    [t]
  );
}
